import {Link, useLocation} from 'react-router-dom';
import {ReactNode, useEffect, useState} from 'react';
import {AuthLayoutFooter} from './auth-layout-footer';
import {useIsDarkMode} from '@common/ui/themes/use-is-dark-mode';
import authBgSvg from './auth-bg.svg';
import {useTrans} from '@common/i18n/use-trans';
import {useSettings} from '@common/core/settings/use-settings';

interface AuthPageProps {
  heading?: ReactNode;
  message?: ReactNode;
  children: ReactNode;
}
export function AuthLayout({heading, children, message}: AuthPageProps) {
  const {branding} = useSettings();
  const isDarkMode = useIsDarkMode();
  const {trans} = useTrans();
  const {pathname, search} = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const splashVideoDesktopLink = branding?.splash_video_desktop_link ?? '';
  const splashVideoMobileLink = branding?.splash_video_mobile_link ?? '';

  const urlParams = new URLSearchParams(search);
  const splashVideoId = urlParams.get('splash_video');
  const isAuthPage =
    pathname === '/login' ||
    pathname === '/register' ||
    pathname === '/forgot-password';
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const videoId = (() => {
    if (splashVideoId) return splashVideoId;
    const defaultLink = isMobile ? splashVideoMobileLink : splashVideoDesktopLink;
    if (!defaultLink) return null;
    const match = defaultLink.match(
      /(?:[?&]v=|youtu\.be\/|embed\/)([\w-]{11})/
    );
    return match ? match[1] : null;
  })();

  useEffect(() => {
    if (!isAuthPage || !videoId) return;
    const oldIframe = document.getElementById('splash-video-iframe');
    if (oldIframe) oldIframe.remove();
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3`;
    iframe.frameBorder = '0';
    iframe.allow = 'autoplay; encrypted-media';
    iframe.allowFullscreen = false;
    iframe.style.position = 'absolute';
    iframe.style.top = '50%';
    iframe.style.left = '50%';
    iframe.style.width = '177.78vh';
    iframe.style.height = '100vh';
    iframe.style.minWidth = '100vw';
    iframe.style.minHeight = '56.25vw';
    iframe.style.transform = 'translate(-50%, -50%)';
    iframe.style.objectFit = 'cover';
    iframe.style.pointerEvents = 'none';
    iframe.id = 'splash-video-iframe';
    const container = document.getElementById('splash-video-container');
    if (container) container.appendChild(iframe);
    let styleTag = document.getElementById('splash-video-style');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'splash-video-style';
      styleTag.innerHTML = `
        @media (max-aspect-ratio: 16/9) {
          #splash-video-iframe {
            width: 100vw !important;
            height: 56.25vw !important;
            min-width: 177.78vh !important;
            min-height: 100vh !important;
          }
        }
      `;
      document.head.appendChild(styleTag);
    }
  }, [isAuthPage, videoId]);

  return (
    <main
      className="flex h-screen flex-col items-center overflow-y-auto bg-alt px-14 pt-70 dark:bg-none md:px-10vw"
      style={{
        backgroundImage:
          !isAuthPage || !videoId
            ? isDarkMode
              ? undefined
              : `url("${authBgSvg}")`
            : undefined,
        zIndex: 1,
        position: 'relative',
      }}
    >
      {isAuthPage && videoId && (
        <>
          <div
            id="splash-video-container"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              overflow: 'hidden',
              zIndex: -1,
              pointerEvents: 'none',
              background: 'black',
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 0,
              pointerEvents: 'none',
              background: 'rgba(0,0,0,0.45)',
            }}
          />
        </>
      )}
      <Link
        to="/"
        className="mb-40 block flex-shrink-0"
        aria-label={trans({message: 'Go to homepage'})}
      >
        <img
          src={isDarkMode ? branding.logo_light : branding?.logo_dark}
          className="m-auto block h-42 w-auto"
          alt=""
        />
      </Link>
      <div
        className="mx-auto w-full max-w-440 rounded-lg px-40 pb-32 pt-40 shadow md:shadow-xl transition-all duration-300 ease-in-out"
        style={{
          backgroundColor: isHovered ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {heading && <h1 className="mb-20 text-xl text-white">{heading}</h1>}
        <div className="text-white">{children}</div>
      </div>
      {message && <div className="mt-36 text-sm text-white">{message}</div>}
      <AuthLayoutFooter />
    </main>
  );
}
